import React from 'react';
import { cn } from './cn';
import { FOCUS_RING } from './Button';

/**
 * Abas do Atmos — sublinhado de 2px na ativa, sem cápsula.
 * Estado é do chamador; aqui só a aparência e a acessibilidade.
 */
export interface TabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Contador opcional à direita do rótulo. */
  count?: React.ReactNode;
  disabled?: boolean;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  className,
}: {
  items: Array<TabItem<T>>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div className={cn('flex gap-0.5 overflow-x-auto border-b border-line', className)} role="tablist">
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            className={cn(
              'flex flex-none items-center gap-2 px-3 pb-[9px] pt-2 text-[13.5px] transition-colors duration-[120ms]',
              FOCUS_RING,
              item.disabled
                ? 'cursor-not-allowed text-faint'
                : active
                  ? 'font-semibold text-fg shadow-[inset_0_-2px_0_var(--primary)]'
                  : 'text-fg-2 hover:text-fg',
            )}
          >
            {item.icon}
            {item.label}
            {item.count !== undefined && item.count !== null && (
              <span className="text-[11px] tabular-nums text-muted">{item.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
